'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { Phone, MapPin, ArrowRight, Check, Loader2 } from 'lucide-react';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';

const StoneOfficeMap = dynamic(
  () => import('./StoneOfficeMap').then((mod) => mod.StoneOfficeMap),
  { 
    ssr: false,
    loading: () => (
      <div className="aspect-square md:aspect-4/3 bg-stone-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 bg-stone-400 animate-pulse mx-auto mb-3" />
          <p className="text-xs uppercase tracking-widest text-stone-500 font-sans">
            Loading map...
          </p>
        </div>
      </div>
    )
  }
);

export interface StoneInvestmentContactSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
  organisationId?: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
type ContactIntent = 'consultation' | 'valuation' | 'buyer' | 'seller' | 'other';

const VALID_INTENTS: ContactIntent[] = ['consultation', 'valuation', 'buyer', 'seller', 'other'];

const INTENT_SUBJECT_FALLBACKS: Record<ContactIntent, string> = {
  consultation: 'Private Consultation',
  valuation: 'Property Valuation',
  buyer: 'Acquiring a Property',
  seller: 'Selling a Property',
  other: 'General Enquiry',
};

// Office location data with actual coordinates (names/locations are translated via messages)
const OFFICES_DATA = {
  marseille: {
    address: '64, rue Sainte, 13001 Marseille',
    latitude: 43.2949,
    longitude: 5.3703,
    phone: '+33 4 88 04 38 07',
  },
  mauritius: {
    address: 'Tamarin, Black River District',
    latitude: -20.3258,
    longitude: 57.3739,
    phone: '+230 5 493 95 90',
  },
};

/**
 * Stone Investment - Contact Section
 * 
 * Concept One Design (Exact Match):
 * - Header with "Contact Us"
 * - Left column: General info, Partners, Form
 * - Right column: Sticky maps
 * - Form with animated underlines
 * - Custom checkboxes
 */
export function StoneInvestmentContactSection({ 
  locale = 'en',
  messages,
  organisationId,
}: StoneInvestmentContactSectionProps) {
  const searchParams = useSearchParams();
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [intent, setIntent] = useState<ContactIntent>('other');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    propertyReference: '',
    message: '',
    privacyAccepted: false,
    newsletterConsent: false,
  });
  const [hasTrackedFormStart, setHasTrackedFormStart] = useState(false);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const contactMessages = (stoneMessages.contact || {}) as Record<string, unknown>;
  const formMessages = (contactMessages.form || {}) as Record<string, unknown>;
  const intentOptions = (formMessages.intentOptions || {}) as Record<string, string>;
  const partnersMessages = (contactMessages.partners || {}) as Record<string, string>;
  const officesMessages = (contactMessages.offices || {}) as Record<string, Record<string, string>>;
  
  const agentsSectionMessages = (stoneMessages.agentsSection || {}) as Record<string, unknown>;
  const agentsList = (agentsSectionMessages.agents || []) as Array<Record<string, string>>;
  const valuationsMessages = (stoneMessages.valuations || {}) as Record<string, unknown>;
  const philosophyMessages = (valuationsMessages.philosophy || {}) as Record<string, string>;
  const trustQuote =
    philosophyMessages.quote ||
    '"The majority of our transactions align perfectly with our initial estimates. This precision is why our clients trust us with their legacy."';

  const formLabel = (key: string, fallback: string): string => {
    const value = formMessages[key];
    return typeof value === 'string' ? value : fallback;
  };

  const intentLabel = useMemo(() => {
    return (value: ContactIntent) =>
      intentOptions[value] || INTENT_SUBJECT_FALLBACKS[value];
  }, [intentOptions]);
  
  const t = (key: string, fallback: string): string => {
    return (contactMessages[key] as string) || fallback;
  };

  // Prefill intent (+ subject) from ?intent= query
  useEffect(() => {
    const raw = searchParams.get('intent');
    if (!raw || !VALID_INTENTS.includes(raw as ContactIntent)) return;
    const nextIntent = raw as ContactIntent;
    setIntent(nextIntent);
    setFormData((prev) => ({
      ...prev,
      subject: prev.subject || intentLabel(nextIntent),
    }));
  }, [searchParams, intentLabel]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (!hasTrackedFormStart) {
      analyticsEvents.formStart('contact_form');
      setHasTrackedFormStart(true);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIntentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextIntent = e.target.value as ContactIntent;
    setIntent(nextIntent);
    setFormData((prev) => ({
      ...prev,
      subject: intentLabel(nextIntent),
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.privacyAccepted) return;

    setFormStatus('submitting');

    try {
      const nameParts = formData.firstName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = formData.lastName.trim() || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');
      const resolvedSubject = formData.subject || intentLabel(intent);

      const messageBody = [
        formData.message,
        `\n---\nIntent: ${intentLabel(intent)} (${intent})`,
        resolvedSubject ? `Subject: ${resolvedSubject}` : '',
        formData.propertyReference ? `Property Reference: ${formData.propertyReference}` : '',
        formData.newsletterConsent ? 'Newsletter: Yes, I wish to receive communications.' : '',
      ].filter(Boolean).join('\n');

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone || undefined,
          message: messageBody,
          intent,
          subject: resolvedSubject,
          locale,
          metadata: { intent, source: 'stone_contact_form' },
          ...(organisationId && { organisationId }),
          turnstileToken: turnstileToken || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to send');

      setEnhancedConversionData({
        email: formData.email,
        phone: formData.phone || undefined,
        firstName,
        lastName: lastName || undefined,
      });

      analyticsEvents.formSubmit('contact_form', window.location.pathname);
      analyticsEvents.generateLead('contact_form', undefined, {
        email: formData.email,
        phone: formData.phone || undefined,
        firstName,
        lastName: lastName || undefined,
      });
      analyticsEvents.custom('generate_lead', {
        lead_source: 'contact_form',
        intent,
      });

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'contact_form',
          form_location: window.location.pathname,
          intent,
        });
      }

      setFormStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        propertyReference: '',
        message: '',
        privacyAccepted: false,
        newsletterConsent: false,
      });
      setIntent('other');
    } catch {
      setFormStatus('error');
    }
  };

  const offices = {
    marseille: {
      name: officesMessages.marseille?.name || 'Marseille Headquarters',
      location: officesMessages.marseille?.location || 'France',
      ...OFFICES_DATA.marseille,
    },
    mauritius: {
      name: officesMessages.mauritius?.name || 'Mauritius Office',
      location: officesMessages.mauritius?.location || 'Indian Ocean',
      ...OFFICES_DATA.mauritius,
    },
  };
  
  return (
    <div className="w-full bg-stone-50 min-h-screen pt-32 pb-24 animate-fade-in">
      {/* Header */}
      <div className="container mx-auto px-6 md:px-12 mb-24">
        <h1 className="font-serif text-6xl md:text-8xl text-stone-900 leading-none mb-8">{t('title', 'Contact Us')}</h1>
        <p className="font-sans font-light text-stone-700 text-lg md:text-xl max-w-2xl leading-relaxed">
          {t('subtitle', 'Whether you are looking to acquire a new property or value an existing asset, our partners are at your disposal to discuss your legacy.')}
        </p>
        {/* Editorial trust line — brand voice until CMS client testimonials are available */}
        <blockquote className="mt-10 max-w-2xl border-l border-stone-300 pl-6">
          <p className="font-serif text-xl md:text-2xl italic text-stone-800 leading-relaxed">
            {trustQuote}
          </p>
        </blockquote>
      </div>

      <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-24">
        
        {/* Left Column: Info & Form */}
        <div className="space-y-24">
          
          {/* General Info */}
          <div>
            <h2 className="font-serif text-3xl text-stone-900 mb-8 italic">{t('companyName', 'Stone Investment')}</h2>
            <div className="space-y-6 font-sans font-light text-stone-600">
              <p className="flex items-start gap-6 group">
                <div className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center shrink-0 group-hover:bg-stone-900 group-hover:text-white transition-colors duration-300">
                  <MapPin size={16} />
                </div>
                <span className="mt-2 text-lg">64, rue Sainte<br/>13001 Marseille - France</span>
              </p>
              <p className="flex items-center gap-6 group">
                <div className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center shrink-0 group-hover:bg-stone-900 group-hover:text-white transition-colors duration-300">
                  <Phone size={16} />
                </div>
                <a href="tel:+33488043807" className="text-lg hover:text-stone-900 transition-colors border-b border-transparent hover:border-stone-900">+33 4 88 04 38 07</a>
              </p>
            </div>
          </div>

          {/* Partners */}
          <div className="border-t border-stone-200 pt-12">
            <p 
              className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-8"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {partnersMessages.ourPartners || 'Our Partners'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
              <div className="group">
                <h3 
                  className="text-3xl md:text-4xl text-stone-900 mb-2 tracking-[-0.02em]"
                  style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 400 }}
                >
                  Pierre Monville
                </h3>
                <p 
                  className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-6 group-hover:text-stone-700 transition-colors duration-300"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {agentsList[0]?.role || 'Founder & Managing Director'}
                </p>
                <div 
                  className="space-y-3 text-stone-600"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <a 
                    href="mailto:p.monville@stoneinvestment.fr" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300 border-b border-transparent hover:border-stone-300 w-fit"
                  >
                    p.monville@stoneinvestment.fr
                  </a>
                  <a 
                    href="tel:+33616641604" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300"
                  >
                    +33 6 16 64 16 04
                  </a>
                </div>
              </div>
              <div className="group">
                <h3 
                  className="text-3xl md:text-4xl text-stone-900 mb-2 tracking-[-0.02em]"
                  style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 400 }}
                >
                  Noël Castinel
                </h3>
                <p 
                  className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-6 group-hover:text-stone-700 transition-colors duration-300"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Provence
                </p>
                <div 
                  className="space-y-3 text-stone-600"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <a 
                    href="mailto:n.castinel@stoneinvestment.fr" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300 border-b border-transparent hover:border-stone-300 w-fit"
                  >
                    n.castinel@stoneinvestment.fr
                  </a>
                  <a 
                    href="tel:+33612564620" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300"
                  >
                    +33 6 12 56 46 20
                  </a>
                </div>
              </div>
              <div className="group">
                <h3 
                  className="text-3xl md:text-4xl text-stone-900 mb-2 tracking-[-0.02em]"
                  style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 400 }}
                >
                  Bertrand Meunier
                </h3>
                <p 
                  className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-6 group-hover:text-stone-700 transition-colors duration-300"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {agentsList[2]?.role || 'Mauritius'}
                </p>
                <div 
                  className="space-y-3 text-stone-600"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <a 
                    href="mailto:b.meunier@stoneinvestment.fr" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300 border-b border-transparent hover:border-stone-300 w-fit"
                  >
                    b.meunier@stoneinvestment.fr
                  </a>
                  <a 
                    href="tel:+23054939590" 
                    className="block text-sm font-light hover:text-stone-900 transition-colors duration-300"
                  >
                    +230 5 493 95 90
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="pt-12 border-t border-stone-200">
            <h2 className="font-serif text-3xl text-stone-900 mb-12 italic">{formLabel('title', 'Drop Us A Line')}</h2>
            
            {formStatus === 'success' ? (
              <div className="text-center py-16 animate-fade-in">
                <div className="w-16 h-16 border border-stone-900 mx-auto mb-8 flex items-center justify-center">
                  <Check size={28} className="text-stone-900" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-stone-900 mb-4 italic">
                  {formLabel('successTitle', 'Message Sent')}
                </h3>
                <p className="font-sans font-light text-stone-600 text-lg max-w-md mx-auto leading-relaxed mb-8">
                  {formLabel('successMessage', 'Thank you for your message. Our team will respond to you shortly.')}
                </p>
                <button
                  type="button"
                  onClick={() => setFormStatus('idle')}
                  className="font-sans text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors border-b border-stone-300 hover:border-stone-900 pb-1"
                >
                  {formLabel('sendAnother', 'Send another message')}
                </button>
              </div>
            ) : (
              <form className="space-y-8" onSubmit={handleSubmit}>
                <div className="group relative">
                  <label htmlFor="stone-contact-intent" className="sr-only">
                    {formLabel('intent', 'How may we help?')}
                  </label>
                  <select
                    id="stone-contact-intent"
                    name="intent"
                    value={intent}
                    onChange={handleIntentChange}
                    className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors font-sans appearance-none cursor-pointer"
                    aria-label={formLabel('intent', 'How may we help?')}
                  >
                    {VALID_INTENTS.map((value) => (
                      <option key={value} value={value}>
                        {intentLabel(value)}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-0 bottom-3 text-stone-400 text-xs">▾</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="group relative">
                    <input 
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder={formLabel('yourName', 'Your Name*')} 
                      className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 font-sans" 
                      required 
                    />
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                  </div>
                  <div className="group relative">
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={formLabel('yourEmail', 'Your Email*')} 
                      className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 font-sans" 
                      required 
                    />
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="group relative">
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={formLabel('yourPhone', 'Your Phone Number')} 
                      className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 font-sans" 
                    />
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                  </div>
                  <div className="group relative">
                    <input 
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder={formLabel('subject', 'Subject')} 
                      className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 font-sans" 
                    />
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                  </div>
                </div>
                <div className="group relative">
                  <input 
                    type="text"
                    name="propertyReference"
                    value={formData.propertyReference}
                    onChange={handleInputChange}
                    placeholder={formLabel('propertyReference', 'Property Reference (Optional)')} 
                    className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 font-sans" 
                  />
                  <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                </div>
                <div className="group relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={formLabel('yourMessage', 'Your Message')} 
                    rows={4} 
                    className="peer w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors placeholder-stone-400 resize-none font-sans"
                    required
                  />
                  <span className="absolute bottom-0 left-0 w-0 h-px bg-stone-900 transition-all duration-500 peer-focus:w-full" />
                </div>

                {/* Checkboxes */}
                <div className="space-y-6 pt-4">
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <div className="relative pt-1">
                      <input
                        type="checkbox"
                        name="privacyAccepted"
                        checked={formData.privacyAccepted}
                        onChange={handleCheckboxChange}
                        className="peer sr-only"
                        required
                      />
                      <div className="w-5 h-5 border border-stone-400 peer-checked:bg-stone-900 peer-checked:border-stone-900 transition-all duration-300" />
                      <Check size={14} className="text-white absolute top-1.5 left-0.5 opacity-0 peer-checked:opacity-100 transition-opacity transform scale-75 peer-checked:scale-100" aria-hidden="true" />
                    </div>
                    <span className="font-sans font-light text-sm text-stone-700 group-hover:text-stone-900 transition-colors leading-relaxed">
                      {formLabel('privacyNotice', 'I read and I accept the privacy notice')} <span className="underline decoration-stone-400 underline-offset-4 hover:decoration-stone-900">{formLabel('moreInfoHere', '(more information here)')}</span> *
                    </span>
                  </label>
                  
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <div className="relative pt-1">
                      <input
                        type="checkbox"
                        name="newsletterConsent"
                        checked={formData.newsletterConsent}
                        onChange={handleCheckboxChange}
                        className="peer sr-only"
                      />
                      <div className="w-5 h-5 border border-stone-400 peer-checked:bg-stone-900 peer-checked:border-stone-900 transition-all duration-300" />
                      <Check size={14} className="text-white absolute top-1.5 left-0.5 opacity-0 peer-checked:opacity-100 transition-opacity transform scale-75 peer-checked:scale-100" aria-hidden="true" />
                    </div>
                    <span className="font-sans font-light text-sm text-stone-700 group-hover:text-stone-900 transition-colors leading-relaxed">
                      {formLabel('newsletterConsent', 'I authorize Stone Investment to contact me by email (selection of properties, newsletters, email alerts)')}
                    </span>
                  </label>
                </div>
                
                <p className="text-xs text-stone-600 italic font-sans">
                  {formLabel('privacyDisclaimer', '* The information collected is necessary for Stone Investment to answer your request. You can read our privacy notice by clicking on the link above. At any time, you have the right to access, modify and delete your data.')}
                </p>

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 px-6 py-4 font-sans">
                    <p className="text-sm text-red-800">
                      {formLabel('errorMessage', 'Something went wrong. Please try again or contact us directly.')}
                    </p>
                  </div>
                )}

                <TurnstileWidget
                  onVerify={handleTurnstileVerify}
                  onExpire={handleTurnstileExpire}
                  className="flex justify-center"
                />

                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="group relative px-12 py-5 bg-stone-900 text-white overflow-hidden shadow-2xl hover:bg-stone-800 transition-all duration-500 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10 text-xs uppercase tracking-widest group-hover:tracking-[0.2em] transition-all duration-500 flex items-center justify-center gap-4 font-sans">
                    {formStatus === 'submitting' ? (
                      <><Loader2 size={16} className="animate-spin" /> {formLabel('sending', 'Sending...')}</>
                    ) : (
                      <>{formLabel('sendMessage', 'Send Message')} <ArrowRight size={16} /></>
                    )}
                  </span>
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Right Column: Interactive Maps */}
        <div className="space-y-8 lg:pt-0">
          <div className="sticky top-32 space-y-8">
            {/* Marseille Office Map */}
            <div className="shadow-xl">
              <StoneOfficeMap
                office={offices.marseille}
                height={320}
                zoom={16}
                translations={{
                  loadingMap: t('loadingMap', 'Loading map...'),
                  directions: t('directions', 'Directions'),
                }}
              />
            </div>
            
            {/* Mauritius Office Map */}
            <div className="shadow-xl">
              <StoneOfficeMap
                office={offices.mauritius}
                height={320}
                zoom={14}
                translations={{
                  loadingMap: t('loadingMap', 'Loading map...'),
                  directions: t('directions', 'Directions'),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
